/* Sprint 2 (FLY2619) , HR Command Center with sticky, actionable widgets.
   Replaces static sidebars. Four filter tiles (NEW / OPEN / IN PROGRESS /
   ANONYMOUS) drive an inline unified case log sorted by CASE AGE (oldest first).
*/
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, StatusPill, PriorityPill, RiskPill } from "../components/UI";
import MonitoringWidget from "../components/MonitoringWidget";
import { toast } from "sonner";
import {
  Sparkles, EyeOff, FilePlus2, FileClock, TimerReset, Lock,
  ArrowRight, AlertTriangle, Search,
} from "lucide-react";

const OPEN_STATUSES = ["open", "under_review", "investigation", "escalated"];

// Sprint 2 widget definition. Each is a filter over the case log.
// "NEW" = created within last 48 hours + still open.
// "IN PROGRESS" = has an active workflow_state.
const WIDGETS = [
  {
    key: "new",
    label: "New cases",
    accent: "bg-indigo-600",
    tint: "bg-indigo-50",
    text: "text-indigo-800",
    Icon: FilePlus2,
    predicate: (c) => {
      const hours = (Date.now() - new Date(c.created_at).getTime()) / 36e5;
      return hours <= 48 && OPEN_STATUSES.includes(c.status);
    },
  },
  {
    key: "open",
    label: "Open cases",
    accent: "bg-slate-900",
    tint: "bg-slate-100",
    text: "text-slate-800",
    Icon: FileClock,
    predicate: (c) => OPEN_STATUSES.includes(c.status),
  },
  {
    key: "in_progress",
    label: "In progress",
    accent: "bg-amber-500",
    tint: "bg-amber-50",
    text: "text-amber-900",
    Icon: TimerReset,
    predicate: (c) => (c.workflow_status === "in_progress"),
  },
  {
    key: "anonymous",
    label: "Anonymous cases",
    accent: "bg-slate-950",
    tint: "bg-slate-100",
    text: "text-slate-900",
    Icon: Lock,
    predicate: (c) => Boolean(c.is_anonymous),
  },
];

export default function HRDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("new");
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/cases", { params: { limit: 200 } })
      .then((r) => { if (mounted) setCases(r.data); })
      .catch(() => toast.error("Failed to load cases"))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const counts = useMemo(() => {
    const c = {};
    for (const w of WIDGETS) c[w.key] = cases.filter(w.predicate).length;
    return c;
  }, [cases]);

  // CASE AGE sort , oldest first (feedback rule).
  const filteredLog = useMemo(() => {
    const w = WIDGETS.find((x) => x.key === active) || WIDGETS[0];
    let list = cases.filter(w.predicate);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((c) =>
        (c.title || "").toLowerCase().includes(n) ||
        (c.case_number || "").toLowerCase().includes(n) ||
        (c.description || "").toLowerCase().includes(n)
      );
    }
    return list.slice().sort((a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [cases, active, q]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="HR Command Center"
        subtitle={`${user.name}, actionable widgets , tap any tile to filter the case log. Sorted by case age, oldest first.`}
      />

      {/* ============ STICKY ACTIONABLE WIDGETS ============ */}
      <div
        className="sticky top-16 z-30 -mx-2 px-2 py-3 mb-4 bg-slate-50/95 backdrop-blur border-b border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3"
        data-testid="hr-widgets"
      >
        {WIDGETS.map(({ key, label, accent, tint, text, Icon }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`widget-${key}`}
              className={
                "text-left rounded-lg border transition-all p-4 flex items-start gap-3 " +
                (isActive
                  ? `${tint} border-transparent shadow-md`
                  : "border-slate-200 bg-white hover:border-slate-400")
              }
            >
              <div className={"w-9 h-9 rounded-lg grid place-items-center shrink-0 " + accent}>
                <Icon size={16} className="text-white" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={"text-[10px] font-mono uppercase tracking-widest " + (isActive ? text : "text-slate-500")}>
                  {label}
                </div>
                <div className="text-3xl font-semibold mt-0.5 text-slate-900">{counts[key] ?? 0}</div>
                {isActive && (
                  <div className="text-[11px] text-slate-600 mt-1 inline-flex items-center gap-1">
                    Filtering below <ArrowRight size={11} />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* ============ SEARCH + AI STRIP ============ */}
      <div className="grid lg:grid-cols-3 gap-6 mb-4">
        <div className="lg:col-span-2 data-cell p-4 flex items-center gap-3">
          <Search size={14} className="text-slate-400" strokeWidth={2} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search within this widget..."
            className="input"
            data-testid="hr-log-search"
          />
          <Link to="/cases" className="btn-ghost text-xs whitespace-nowrap">
            Open full case log <ArrowRight size={12} />
          </Link>
        </div>
        <MonitoringWidget />
      </div>

      {/* ============ AI STRIP ============ */}
      <div className="ai-surface tracing-beam p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} strokeWidth={1.75} className="text-blue-400" />
          <span className="eyebrow text-blue-300">Sentinel Analytics</span>
        </div>
        <div className="font-editorial text-base text-slate-100 leading-snug">
          Focus first on the oldest open item on your board.
        </div>
      </div>

      {/* ============ UNIFIED CASE LOG ============ */}
      <UnifiedCaseLog cases={filteredLog} loading={loading} />
    </div>
  );
}

// Sprint 3 companion , shared row structure with CaseList (9 columns + SLA + lock).
function UnifiedCaseLog({ cases, loading }) {
  if (loading) {
    return <div className="data-cell p-16 text-center text-slate-500 font-mono text-sm">Loading cases...</div>;
  }
  if (cases.length === 0) {
    return (
      <div className="data-cell p-16 text-center text-slate-500 font-mono text-sm">
        Nothing matches this filter. Try another widget.
      </div>
    );
  }
  return (
    <div className="border border-slate-200 bg-white overflow-x-auto rounded" data-testid="hr-case-log">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <th className="px-3 py-3 eyebrow">Case #</th>
            <th className="px-3 py-3 eyebrow">Title</th>
            <th className="px-3 py-3 eyebrow">Allegations</th>
            <th className="px-3 py-3 eyebrow">Reporter</th>
            <th className="px-3 py-3 eyebrow">Assigned</th>
            <th className="px-3 py-3 eyebrow">Priority</th>
            <th className="px-3 py-3 eyebrow">Case age</th>
            <th className="px-3 py-3 eyebrow">SLA</th>
            <th className="px-3 py-3 eyebrow">Status</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((c) => <CaseRow key={c.id} c={c} />)}
        </tbody>
      </table>
    </div>
  );
}

export function CaseRow({ c }) {
  const ageHours = Math.max(0, (Date.now() - new Date(c.created_at).getTime()) / 36e5);
  const slaMs = c.sla_due_at ? new Date(c.sla_due_at).getTime() - Date.now() : null;
  const slaHours = slaMs != null ? slaMs / 36e5 : null;
  const slaBreached = slaHours != null && slaHours < 0;
  const slaWarn = slaHours != null && slaHours >= 0 && slaHours < 48;

  return (
    <tr className="row-hover border-b border-slate-100 last:border-b-0" data-testid={`case-row-${c.id}`}>
      <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          {c.is_anonymous && (
            <span title="Anonymous report" data-testid={`case-lock-${c.id}`}>
              <Lock size={12} className="text-amber-600" strokeWidth={2.25} />
            </span>
          )}
          <Link to={`/cases/${c.id}`} className="text-slate-900 hover:underline font-semibold">
            {c.case_number}
          </Link>
        </div>
      </td>
      <td className="px-3 py-3 max-w-[220px] truncate">
        <Link to={`/cases/${c.id}`} className="font-medium hover:underline text-slate-900">{c.title}</Link>
      </td>
      <td className="px-3 py-3 text-xs text-slate-600">
        {(c.allegation_tags || []).slice(0, 2).map((t) => (
          <span key={t} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 font-mono text-[10px]">{t}</span>
        ))}
        {(c.allegation_tags || []).length > 2 && (
          <span className="text-[10px] text-slate-500">+{c.allegation_tags.length - 2}</span>
        )}
        {(!c.allegation_tags || c.allegation_tags.length === 0) && (
          <span className="text-slate-500">{c.category}</span>
        )}
      </td>
      <td className="px-3 py-3 text-slate-700 text-xs truncate max-w-[140px]">{c.reporter_name || "Anonymous"}</td>
      <td className="px-3 py-3 text-slate-700 text-xs truncate max-w-[140px]">{c.assigned_hr_name || <span className="text-slate-400">Unassigned</span>}</td>
      <td className="px-3 py-3"><PriorityPill priority={c.priority} /></td>
      <td className="px-3 py-3 text-xs font-mono text-slate-700 whitespace-nowrap">
        {ageHours < 24 ? `${ageHours.toFixed(0)}h` : `${(ageHours / 24).toFixed(0)}d`}
      </td>
      <td className="px-3 py-3 text-xs font-mono whitespace-nowrap">
        {slaHours == null ? (
          <span className="text-slate-400">,</span>
        ) : slaBreached ? (
          <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
            <AlertTriangle size={11} strokeWidth={2.5} /> Breached
          </span>
        ) : slaWarn ? (
          <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid={`sla-red-${c.id}`}>
            <AlertTriangle size={11} strokeWidth={2.5} /> {slaHours.toFixed(0)}h left
          </span>
        ) : (
          <span className="text-slate-700">
            {slaHours < 48 ? `${slaHours.toFixed(0)}h` : `${(slaHours / 24).toFixed(0)}d`} left
          </span>
        )}
      </td>
      <td className="px-3 py-3"><StatusPill status={c.status} /></td>
    </tr>
  );
}
